import type { PayloadAction } from '@reduxjs/toolkit';
import type { UnitedCanvasReducerState } from './unitedCanvasReducer';
import type { RootState } from '..';
import type { FreePosition } from '../../types/FreePosition';
import type { FunnelMapText } from '../../types/FunnelMapText';

export const textNodesReducer = {
  addTextNode: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      textNode: FunnelMapText;
    }>,
  ) => {
    if (!state.funnelMap) {
      return;
    }

    const { textNode } = action.payload;

    // Initialize text nodes if they don't exist
    if (!state.funnelMap.texts) {
      state.funnelMap.texts = {};
    }

    state.funnelMap.texts[textNode.textId] = textNode;
  },

  changeTextNodeData: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      textNodeId: string;
      richText: string;
      textScale: number; // Float
      height: number;
    }>,
  ) => {
    if (!state.funnelMap?.texts) {
      return;
    }

    const { textNodeId, richText, textScale, height } = action.payload;
    const textNode = state.funnelMap.texts[textNodeId];

    if (textNode) {
      textNode.richText = richText;
      textNode.textScale = textScale;
      textNode.height = height;
    }
  },

  moveTextNodeOnCanvas: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      textNodeId: string;
      newPosition: FreePosition;
      sectionId?: string;
    }>,
  ) => {
    if (!state.funnelMap?.texts) {
      return;
    }

    const { textNodeId, newPosition, sectionId } = action.payload;
    const textNode = state.funnelMap.texts[textNodeId];

    if (textNode) {
      textNode.xyPosition = newPosition;
      textNode.sectionId = sectionId;
    }
  },

  resizeTextNodeOnCanvas: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      textNodeId: string;
      width: number;
      height: number;
      xyPosition: FreePosition;
      textScale: number; // Float
    }>,
  ) => {
    if (!state.funnelMap?.texts) {
      return;
    }

    const { textNodeId, width, height, xyPosition, textScale } = action.payload;
    const textNode = state.funnelMap.texts[textNodeId];

    if (textNode) {
      textNode.width = width;
      textNode.height = height;
      textNode.xyPosition = xyPosition;
      textNode.textScale = textScale;
    }
  },

  removeTextNodeFromCanvas: (
    state: UnitedCanvasReducerState,
    action: PayloadAction<{
      textNodeId: string;
    }>,
  ) => {
    if (!state.funnelMap?.texts) {
      return;
    }

    const { textNodeId } = action.payload;
    delete state.funnelMap.texts[textNodeId];
  },
};

// Selectors
export const selectTextNodes = (state: RootState) => state.unitedCanvas.present.funnelMap?.texts;

export const selectTextNodeById = (textNodeId: string) => (state: RootState) =>
  state.unitedCanvas.present.funnelMap?.texts?.[textNodeId];
